<template>
    <broadcast-card :eyebrow="team.name" title="Χάρτης σουτ">
        <template #head-left>
            <img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt="" class="shooting-screen__logo">
        </template>
        <div class="shooting-screen" :style="{ '--team-color': color }">

            <div class="shooting-screen__body">
                <div class="shooting-screen__court">
                    <transition name="ss-fade" mode="out-in">
                        <shot-court :key="mode" :shots="shots" :mode="mode" :dot-size="22" />
                    </transition>
                    <!-- what is on the court right now, since the view changes by itself -->
                    <div class="ss-mode">
                        <span :class="{ 'ss-mode--on': mode === 'dots' }">Σημεία</span>
                        <span :class="{ 'ss-mode--on': mode === 'zones' }">Ζώνες</span>
                    </div>
                </div>

                <div class="shooting-screen__side">
                    <div v-for="row in rows" :key="row.key" class="ss-row">
                        <div class="ss-row__top">
                            <span class="ss-row__label">{{ row.label }}</span>
                            <span class="ss-row__detail">{{ row.made }}/{{ row.attempts }}</span>
                            <span class="ss-row__pct">{{ row.pct }}%</span>
                        </div>
                        <div class="ss-row__bar">
                            <span :style="{ width: `${row.pct}%` }" />
                        </div>
                    </div>

                    <transition name="ss-fade" mode="out-in">
                        <div v-if="mode === 'dots'" key="dots" class="ss-legend">
                            <span><i class="ss-dot" :style="{ background: color, borderColor: color }" /> Εύστοχο</span>
                            <span><i class="ss-dot" :style="{ borderColor: color }" /> Άστοχο</span>
                        </div>
                        <div v-else key="zones" class="ss-legend">
                            <span><i class="ss-swatch ss-swatch--hot" /> Πολύ καλό</span>
                            <span><i class="ss-swatch ss-swatch--ok" /> Μέτριο</span>
                            <span><i class="ss-swatch ss-swatch--cold" /> Χαμηλό</span>
                        </div>
                    </transition>

                    <div v-if="topScorers.length" class="ss-scorers">
                        <div class="ss-scorers__title">Κορυφαίοι σκόρερ</div>
                        <div v-for="player in topScorers" :key="player.key" class="ss-scorer">
                            <span class="ss-scorer__photo"><img :src="player.imageSrc" alt="" @error="$event.target.src = defaultPlayerImg"></span>
                            <span class="ss-scorer__info">
                                <span class="ss-scorer__name">{{ player.name }}</span>
                                <span class="ss-scorer__line">
                                    <span>Σουτ <b>{{ player.fgMade }}/{{ player.fgAttempts }}</b></span>
                                    <span>Τρίποντα <b>{{ player.threeMade }}/{{ player.threeAttempts }}</b></span>
                                    <span>Βολές <b>{{ player.ftMade }}/{{ player.ftAttempts }}</b></span>
                                </span>
                            </span>
                            <span class="ss-scorer__points">{{ player.points }}<small>π</small></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </broadcast-card>
</template>

<script>
    import BroadcastCard from '@/components/BroadcastCard'
    import ShotCourt from '@/components/ShotCourt'
    import { BACKGROUNDS, getMatchColors } from '@/utils/colors'
    import { buildShots, getPlayerImageSrc, getPlayerKey } from '@/utils/shotChart'

    const ROWS = [
        { key: 'fg', label: 'Σουτ' },
        { key: 'two', label: 'Δίποντα', made: 'twoPointsMade', attempts: 'twoPointAttempts' },
        { key: 'three', label: 'Τρίποντα', made: 'threePointsMade', attempts: 'threePointAttempts' },
        { key: 'ft', label: 'Βολές', made: 'freeThrowsMade', attempts: 'freeThrowAttempts' }
    ]

    // Live-stream break screen: the shots of one team on the court, with its shooting percentages
    export default {
        name: 'ShootingChartScreen',
        components: {
            BroadcastCard,
            ShotCourt
        },
        props: {
            match: {
                type: Object,
                default: () => ({})
            },
            // 'team1' | 'team2'
            teamKey: {
                type: String,
                default: 'team1'
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                // the court switches between every shot and the zone percentages while on screen
                mode: 'dots',
                modeTimer: null,
                modeIntervalMs: 10000
            }
        },
        computed: {
            team() {
                return (this.match && this.match[this.teamKey]) || {}
            },
            color() {
                // the dots are drawn on the parquet
                return getMatchColors(this.match && this.match.team1, this.match && this.match.team2, BACKGROUNDS.court, 1.6)[this.teamKey]
            },
            players() {
                return Array.isArray(this.team.players) ? this.team.players : []
            },
            shots() {
                return buildShots(this.players, this.teamKey, this.color)
            },
            // the official box score totals, so free throws are included too
            totals() {
                const totals = {}
                this.players.forEach((player) => {
                    const stats = (player.stats && player.stats[0]) || {}
                    ROWS.forEach((row) => {
                        if (!row.made) return
                        totals[row.made] = (totals[row.made] || 0) + (Number(stats[row.made]) || 0)
                        totals[row.attempts] = (totals[row.attempts] || 0) + (Number(stats[row.attempts]) || 0)
                    })
                })
                return totals
            },
            rows() {
                return ROWS.map((row) => {
                    const made = row.made
                        ? this.totals[row.made] || 0
                        : (this.totals.twoPointsMade || 0) + (this.totals.threePointsMade || 0)
                    const attempts = row.attempts
                        ? this.totals[row.attempts] || 0
                        : (this.totals.twoPointAttempts || 0) + (this.totals.threePointAttempts || 0)
                    return { ...row, made, attempts, pct: attempts ? Math.round(made / attempts * 100) : 0 }
                })
            },
            topScorers() {
                return this.players
                    .map((player, index) => {
                        const stats = (player.stats && player.stats[0]) || {}
                        return {
                            key: getPlayerKey(player, index),
                            name: `${player.name ? `${player.name.charAt(0)}. ` : ''}${player.surname || player.lastName || ''}`.trim(),
                            imageSrc: getPlayerImageSrc(player, this.defaultPlayerImg),
                            points: Number(stats.points) || 0,
                            fgMade: (Number(stats.twoPointsMade) || 0) + (Number(stats.threePointsMade) || 0),
                            fgAttempts: (Number(stats.twoPointAttempts) || 0) + (Number(stats.threePointAttempts) || 0),
                            threeMade: Number(stats.threePointsMade) || 0,
                            threeAttempts: Number(stats.threePointAttempts) || 0,
                            ftMade: Number(stats.freeThrowsMade) || 0,
                            ftAttempts: Number(stats.freeThrowAttempts) || 0
                        }
                    })
                    .filter(player => player.points > 0)
                    .sort((a, b) => b.points - a.points)
                    .slice(0, 3)
            }
        },
        mounted() {
            this.modeTimer = setInterval(() => {
                this.mode = this.mode === 'dots' ? 'zones' : 'dots'
            }, this.modeIntervalMs)
        },
        beforeDestroy() {
            clearInterval(this.modeTimer)
        }
    }
</script>

<style scoped>
.shooting-screen__logo {
    width: 84px;
    height: 84px;
    padding: 5px;
    border-radius: 50%;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.16);
    object-fit: contain;
}

.shooting-screen__body {
    display: grid;
    grid-template-columns: 56vh minmax(0, 1fr);
    gap: 40px;
    align-items: start;
}

.shooting-screen__court {
    position: relative;
    width: 56vh;
}

.ss-mode {
    /* bottom left: the empty back court, away from the shots and the zone badges */
    position: absolute;
    bottom: 14px;
    left: 14px;
    z-index: 3;
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    border-radius: 999px;
    background: rgba(13, 13, 15, 0.85);
    font-size: 0.95rem;
    font-weight: 800;
}

.ss-mode span {
    padding: 4px 12px;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.6);
    transition: background 0.3s ease, color 0.3s ease;
}

.ss-mode .ss-mode--on {
    background: linear-gradient(90deg, #ef4444, #ff7133, orange);
    color: #fff;
}

.ss-fade-enter-active,
.ss-fade-leave-active {
    transition: opacity 0.4s ease;
}

.ss-fade-enter,
.ss-fade-leave-to {
    opacity: 0;
}

.ss-swatch {
    width: 18px;
    height: 18px;
    border-radius: 5px;
}

.ss-swatch--hot {
    background: #16a34a;
}

.ss-swatch--ok {
    background: #f59e0b;
}

.ss-swatch--cold {
    background: #dc2626;
}

.shooting-screen__side {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

/* ---------- Percentages ---------- */
.ss-row__top {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 6px;
}

.ss-row__label {
    flex: 1;
    color: rgba(0, 0, 0, 0.6);
    font-size: 1.5rem;
    font-weight: 800;
}

.ss-row__detail {
    color: rgba(0, 0, 0, 0.5);
    font-size: 1.3rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.ss-row__pct {
    min-width: 92px;
    color: #111;
    font-size: 2.2rem;
    font-weight: 800;
    line-height: 1;
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.ss-row__bar {
    height: 12px;
    border-radius: 6px;
    background: #f1f1f3;
    overflow: hidden;
}

.ss-row__bar span {
    display: block;
    height: 100%;
    border-radius: 6px;
    background: var(--team-color);
}

.ss-legend {
    display: flex;
    gap: 24px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 1.1rem;
    font-weight: 700;
}

.ss-legend span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.ss-dot {
    width: 20px;
    height: 20px;
    border: 4px solid;
    border-radius: 50%;
    background: #fff;
}

/* ---------- Top scorers ---------- */
.ss-scorers {
    padding-top: 14px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.ss-scorers__title {
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.ss-scorer {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 6px 0;
}

.ss-scorer__photo {
    flex: 0 0 auto;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #f1f1f3;
    box-shadow: inset 0 0 0 3px var(--team-color);
    overflow: hidden;
}

.ss-scorer__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

/* name on one line, the shooting of the player under it */
.ss-scorer__info {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.ss-scorer__name {
    color: #2b2b2e;
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.15;
    white-space: nowrap;
}

.ss-scorer__line {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 12px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.95rem;
    font-weight: 700;
}

.ss-scorer__line b {
    color: #2b2b2e;
    font-variant-numeric: tabular-nums;
}

.ss-scorer__points {
    min-width: 64px;
    color: #111;
    font-size: 1.9rem;
    font-weight: 800;
    text-align: right;
}

.ss-scorer__points small {
    margin-left: 2px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 1rem;
}

/* zone badges big enough to read on a TV */
.shooting-screen__court ::v-deep .zone-badge {
    min-width: 76px;
    padding: 5px 10px;
    border-radius: 12px;
}

.shooting-screen__court ::v-deep .zone-badge b {
    font-size: 1.25rem;
}

.shooting-screen__court ::v-deep .zone-badge small {
    font-size: 1rem;
}
</style>
